import React, { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Check, Truck, Phone, ShieldCheck, MessageSquare } from "lucide-react";
import Seo, { productJsonLd, breadcrumbJsonLd } from "@/components/Seo";
import { ProductCard } from "@/components/common";
import { getProduct, getBrand, PRODUCTS, SIZES, SITE } from "@/mock";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProduct(slug);
  const [activeImg, setActiveImg] = useState(0);

  const sizesList = product && product.sizes && product.sizes.length ? product.sizes : SIZES;
  const [size, setSize] = useState(sizesList.includes("Queen") ? "Queen" : sizesList[0]);

  useEffect(() => {
    setSize(sizesList.includes("Queen") ? "Queen" : sizesList[0]);
    setActiveImg(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!product) return <Navigate to="/mattresses" replace />;
  const brand = getBrand(product.brand);
  const related = PRODUCTS.filter((p) => p.brand === product.brand && p.slug !== product.slug).slice(0, 3);
  const gallery = product.gallery && product.gallery.length ? product.gallery : [product.image];
  const isBase = product.category === "adjustable-bases";
  const catName = isBase ? "Adjustable Beds & Foundations" : "Mattresses";
  const from = product.priceFrom ? "From " : "";
  const variant = (product.variants && product.variants[size]) || null;
  const unitPrice = variant ? variant.price : product.price;
  const unitCompare = variant ? variant.compareAt : product.compareAt;
  const showFrom = variant ? "" : from;
  const savePct = unitCompare ? Math.round(((unitCompare - unitPrice) / unitCompare) * 100) : 0;

  const crumbs = [
    { name: "Home", path: "/" },
    { name: catName, path: `/${product.category}` },
    { name: product.name, path: `/products/${product.slug}` },
  ];

  return (
    <>
      <Seo
        title={product.name}
        description={product.description}
        path={`/products/${product.slug}`}
        image={product.image}
        type="product"
        jsonLd={[productJsonLd(product), breadcrumbJsonLd(crumbs)]}
      />

      <div className="mx-auto grid max-w-7xl gap-6 px-4 pb-6 pt-4 sm:gap-10 sm:px-6 sm:py-10 lg:grid-cols-2 lg:px-8">
        {/* Gallery */}
        <div>
          <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white">
            <img src={gallery[activeImg]} alt={product.name} data-testid="product-main-image" className="aspect-square w-full object-cover" />
          </div>
          {gallery.length > 1 && (
            <div className="mt-4 flex flex-wrap gap-3">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setActiveImg(i)}
                  aria-label={`View image ${i + 1}`}
                  data-testid={`gallery-thumb-${i}`}
                  className={`h-20 w-20 overflow-hidden rounded-lg border-2 ${i === activeImg ? "border-[#D0021B]" : "border-neutral-200"}`}
                >
                  <img src={g} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          {brand && (
            <Link to={`/brands/${brand.slug}`} className="text-sm font-bold uppercase tracking-widest text-[#D0021B] hover:underline">
              {brand.name}
            </Link>
          )}
          <h1 className="mt-2 text-3xl font-bold leading-tight text-neutral-900 lg:text-4xl" data-testid="product-title">
            {product.name}
          </h1>
          {product.sku && <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-neutral-400">SKU: {product.sku}</p>}

          <div className="mt-5 flex flex-wrap items-baseline gap-x-3 gap-y-1" data-testid="product-price">
            <span className="text-4xl font-bold text-neutral-900">{showFrom}${unitPrice.toLocaleString()}</span>
            {unitCompare && (
              <span className="text-xl font-medium text-neutral-400 line-through">{showFrom}${unitCompare.toLocaleString()}</span>
            )}
            {savePct > 0 && (
              <span className="rounded bg-[#D0021B]/10 px-2 py-1 text-sm font-bold text-[#D0021B]">Save {savePct}%</span>
            )}
          </div>

          <div className="mt-4 flex flex-wrap gap-2 text-xs font-semibold uppercase tracking-wide text-neutral-600">
            {product.type && <span className="rounded-full bg-neutral-100 px-3 py-1">{product.type}</span>}
            {product.firmness && product.firmness !== "-" && <span className="rounded-full bg-neutral-100 px-3 py-1">{product.firmness}</span>}
            {product.height && product.height !== "-" && <span className="rounded-full bg-neutral-100 px-3 py-1">{product.height} profile</span>}
          </div>

          <p className="mt-5 leading-relaxed text-neutral-600">{product.description}</p>

          {/* Size selector */}
          <div className="mt-6">
            <span className="mb-2 block text-sm font-bold uppercase tracking-wide text-neutral-800">Choose your Size</span>
            <div className="flex flex-wrap gap-2" data-testid="size-selector">
              {sizesList.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSize(s)}
                  aria-pressed={size === s}
                  data-testid={`size-option-${s.toLowerCase().replace(/\s+/g, "-")}`}
                  className={`rounded-lg border px-4 py-2 text-sm font-semibold ${size === s ? "border-[#D0021B] bg-[#D0021B] text-white" : "border-neutral-300 text-neutral-700 hover:border-[#D0021B]"}`}
                >
                  {s}
                </button>
              ))}
            </div>
            <p className="mt-2 text-sm text-neutral-500">Selected: <span className="font-semibold text-neutral-800">{size}</span></p>
          </div>

          {/* CTAs (lead-gen, no checkout) */}
          <div className="mt-7 flex flex-wrap gap-3">
            <Link
              to={`/contact?product=${encodeURIComponent(product.name)}&size=${encodeURIComponent(size)}`}
              data-testid="contact-about-product"
              className="inline-flex items-center gap-2 rounded-full bg-[#D0021B] px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-white hover:bg-[#a80016]"
            >
              <MessageSquare className="h-4 w-4" aria-hidden="true" /> Contact Us About This {isBase ? "Base" : "Mattress"}
            </Link>
            <a
              href={SITE.phoneHref}
              data-testid="call-best-price"
              className="inline-flex items-center gap-2 rounded-full border-2 border-neutral-900 px-8 py-3.5 text-sm font-bold uppercase tracking-wide text-neutral-900 hover:bg-neutral-900 hover:text-white"
            >
              <Phone className="h-4 w-4" aria-hidden="true" /> Call for Best Price
            </a>
          </div>

          <ul className="mt-6 space-y-2 text-sm text-neutral-700">
            <li className="flex items-center gap-2"><Truck className="h-4 w-4 text-[#D0021B]" aria-hidden="true" /> Local delivery, set-up &amp; old-mattress removal</li>
            <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#D0021B]" aria-hidden="true" /> 60-day price guarantee &amp; special financing</li>
          </ul>

          {product.features?.length > 0 && (
            <div className="mt-8 rounded-xl border border-neutral-200 bg-neutral-50 p-5">
              <h2 className="text-sm font-bold uppercase tracking-wide text-neutral-800">Key Features</h2>
              <ul className="mt-3 grid gap-2 sm:grid-cols-2">
                {product.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-neutral-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#D0021B]" aria-hidden="true" /> {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {related.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-12 pt-4 sm:px-6 sm:py-14 lg:px-8">
          <h2 className="text-2xl font-bold uppercase text-neutral-900">More from {brand?.name}</h2>
          <div className="mt-6 grid grid-cols-2 gap-5 md:grid-cols-3">
            {related.map((p) => (<ProductCard key={p.slug} product={p} />))}
          </div>
        </section>
      )}
    </>
  );
}
